import React, { useMemo, useState } from 'react';
import { StrategyChart, DatasetConfig } from './StrategyChart';
import { TimeGranularity } from './StrategiesUtils';
import { aggregateToBoundaryPeriods, aggregateToSingleBucket, resolveBoundaryMonths } from '../../../utils/timeGranularity';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { useBaseHedgeMaterials, useDerivedMaterials } from '../../../hooks/useBaseHedgeMaterials';
import { useDemandPositions } from '../../../hooks/useDemandPositions';
import { useSupplyPositions } from '../../../hooks/useSupplyPositions';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import { useAccountStore } from '@components/Stores/accountStore';
import { BufferStrategyConfig } from '../../../sp-types/accountTypes';
import { useAppSession } from '../../../contexts/AppSessionContext';
import { useQuery } from 'react-query';
import { getHedgePositions, HedgePosition } from '../../../services/hedgesService';
import { ChartControls } from './ChartControls';

export interface BaseMaterialMonthData {
  month: string;
  // Dynamic keys: supply_<formulaCode> and demand_<formulaCode> per derived material
  [key: string]: any;
  // Totals
  totalSupply: number;
  totalDemand: number;
  buffer: number;
  fullDemand: number; // totalDemand + buffer
  hedges: number;
  totalCoverage: number;
  covPercentage: number;
  hedgedPercentage: number;
  uncoveredDemand: number;
  // Detail objects for stock panel
  supplyByMaterial: Record<string, number>;
  demandByMaterial: Record<string, number>;
  // Raw (not ratio-converted) values for stock panel
  rawSupplyByMaterial: Record<string, number>;
  rawDemandByMaterial: Record<string, number>;
  bufferByMaterial: Record<string, number>;
  rawBufferByMaterial: Record<string, number>;
  ratioByMaterial: Record<string, number>;
  absoluteMonth?: number;
}

interface Props {
  accountId: number;
  baseMaterialId: string;
  baseMaterialPrice: number;
  unit?: IMassUnitObject;
  terminals?: number[] | null;
  timeGranularity?: TimeGranularity;
  onTimeChange?: (_time: TimeGranularity) => void;
  onMonthSelect?: (_index: number) => void;
  selectedIndex?: number;
  onDataCalculated?: (_data: {
    chartData: BaseMaterialMonthData[];
    derivedMaterials: Array<{ formula_code: string; formula_name: string; price_ratio: number }>;
  }) => void;
}

export const BaseMaterialManagement: React.FC<Props> = ({
  accountId,
  baseMaterialId,
  baseMaterialPrice,
  unit,
  terminals,
  timeGranularity = 'Month',
  onMonthSelect,
  selectedIndex,
  onTimeChange,
  onDataCalculated,
}) => {
  const { formulaColorDictionary, account } = useAccountStore();
  const { user } = useAppSession();
  const numericAccountId = accountId;
  const BUFFER_DEFAULT = 60;

  // Fetch base material template and derived materials
  const { data: baseHedgeMaterials } = useBaseHedgeMaterials(numericAccountId);
  const baseTemplate = baseHedgeMaterials?.[0];
  const templateId = baseTemplate?.id ? parseInt(baseTemplate.id, 10) : undefined;
  const { data: derivedMaterials } = useDerivedMaterials(templateId);

  // Fetch demand and supply positions (all materials)
  const { data: demandPositions = [] } = useDemandPositions();
  const { data: supplyPositions = [] } = useSupplyPositions();
  const { data: formulasPricesData } = useFormulasPrices();

  // Fetch actual base hedges
  const { data: baseHedges = [] } = useQuery<HedgePosition[]>(
    ['base-hedges', numericAccountId],
    () => getHedgePositions({ accountId: numericAccountId }),
    {
      enabled: !!numericAccountId,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  const pricesMap = useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    const map: Record<string, number> = {};
    Object.entries(formulasPricesData.pricesMap).forEach(([code, info]) => {
      map[code] = info.price;
    });
    return map;
  }, [formulasPricesData]);

  const terminalCount = useMemo(
    () => (terminals || []).length,
    [terminals],
  );

  // Calculate base material view data
  const chartData = useMemo<BaseMaterialMonthData[]>(() => {
    if (!derivedMaterials || derivedMaterials.length === 0 || !baseTemplate) {
      return [];
    }

    const baseFormulaCode = baseTemplate.formula_code;
    const now = new Date();
    const monthsToCalculate = 24;

    // Resolve the ratio exactly like the derivation engine: linked → live linked-ratio
    // price (falls back to the stored ratio), unlinked → the stored price_ratio. Keeps
    // this view consistent with the Strauss table and the HedgesDeriving chart.
    const resolveRatio = (dm: { linked_ratio_code?: string | null; price_ratio: number }) => {
      const live = dm.linked_ratio_code ? pricesMap[dm.linked_ratio_code] : undefined;
      return live && live > 0 ? live : (Number(dm.price_ratio) || 1);
    };

    // Filter base hedges only (by base formula code)
    const baseOnlyHedges = baseHedges.filter(h => h.formula_code === baseFormulaCode);

    // Get buffer config per derived material from account settings
    const bufferConfigs: Record<string, { baseAmount: number; lookaheadMonths: number; safetyPercentage: number }> = {};
    derivedMaterials.forEach(dm => {
      const fta = account.formulasToAccount.find(f => f.formulaId === dm.formula_code);
      const raw = fta?.bufferStrategy ?? BUFFER_DEFAULT;
      if (typeof raw === 'number') {
        bufferConfigs[dm.formula_code] = { baseAmount: raw, lookaheadMonths: 0, safetyPercentage: 0 };
      } else {
        bufferConfigs[dm.formula_code] = raw as { baseAmount: number; lookaheadMonths: number; safetyPercentage: number };
      }
    });

    // Initialize storage tracking per derived material
    const currentStorage: Record<string, number> = {};

    // Get on-hand storage for each derived material
    derivedMaterials.forEach(dm => {
      const onHandSupply = supplyPositions
        .filter(s =>
          s.formula_code === dm.formula_code &&
          !s.is_purchase_order &&
          !s.exclude_from_calcs
        )
        .reduce((sum, s) => sum + (typeof s.quantity === 'number' ? s.quantity : parseFloat(String(s.quantity)) || 0), 0);
      currentStorage[dm.formula_code] = onHandSupply;
    });

    // Get on-hand base hedges (maturity in the past or current month are "on-hand")
    const result: BaseMaterialMonthData[] = [];

    // On-hand entry
    const onHandItem: BaseMaterialMonthData = {
      month: 'On-Hand',
      totalSupply: 0,
      totalDemand: 0,
      buffer: 0,
      fullDemand: 0,
      hedges: 0,
      totalCoverage: 0,
      covPercentage: 0,
      hedgedPercentage: 0,
      uncoveredDemand: 0,
      supplyByMaterial: {},
      demandByMaterial: {},
      rawSupplyByMaterial: {},
      rawDemandByMaterial: {},
      bufferByMaterial: {},
      rawBufferByMaterial: {},
      ratioByMaterial: {},
    };

    let totalOnHandSupply = 0;
    derivedMaterials.forEach(dm => {
      const ratio = resolveRatio(dm);
      const rawSupply = currentStorage[dm.formula_code] || 0;
      const baseEquivSupply = rawSupply * ratio;
      onHandItem[`supply_${dm.formula_code}`] = baseEquivSupply;
      onHandItem.supplyByMaterial[dm.formula_code] = baseEquivSupply;
      onHandItem.rawSupplyByMaterial[dm.formula_code] = rawSupply;
      onHandItem.ratioByMaterial[dm.formula_code] = ratio;
      onHandItem[`demand_${dm.formula_code}`] = 0;
      onHandItem.demandByMaterial[dm.formula_code] = 0;
      onHandItem.rawDemandByMaterial[dm.formula_code] = 0;
      onHandItem.bufferByMaterial[dm.formula_code] = 0;
      onHandItem.rawBufferByMaterial[dm.formula_code] = 0;
      totalOnHandSupply += baseEquivSupply;
    });

    onHandItem.totalSupply = totalOnHandSupply;
    onHandItem.totalCoverage = totalOnHandSupply;
    result.push(onHandItem);

    // Monthly data
    for (let i = 0; i < monthsToCalculate; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const currYear = monthDate.getFullYear();
      const currMonth = monthDate.getMonth();

      const monthKey = monthDate
        .toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
        .replace(' ', '')
        .replace("'", '');

      const monthItem: BaseMaterialMonthData = {
        month: monthKey,
        totalSupply: 0,
        totalDemand: 0,
        buffer: 0,
        fullDemand: 0,
        hedges: 0,
        totalCoverage: 0,
        covPercentage: 0,
        hedgedPercentage: 0,
        uncoveredDemand: 0,
        supplyByMaterial: {},
        demandByMaterial: {},
        rawSupplyByMaterial: {},
        rawDemandByMaterial: {},
        bufferByMaterial: {},
        rawBufferByMaterial: {},
        ratioByMaterial: {},
      };

      let monthTotalSupply = 0;
      let monthTotalDemand = 0;

      // For each derived material, calculate supply and demand in base terms
      derivedMaterials.forEach(dm => {
        const formulaCode = dm.formula_code;
        const ratio = resolveRatio(dm);

        // Get demand for this material this month
        const rawDemand = demandPositions
          .filter(d => {
            if (!d.delivery_period) return false;
            const [year, month] = d.delivery_period.split('-').map(Number);
            return d.formula_code === formulaCode &&
              year === currYear &&
              month === currMonth + 1;
          })
          .reduce((sum, d) => sum + (typeof d.quantity === 'number' ? d.quantity : parseFloat(String(d.quantity)) || 0), 0);

        // Get supply (POs) for this material this month
        const rawSupplyPOs = supplyPositions.filter(s => {
          const [year, month] = s.delivery_or_available_period.split('-').map(Number);
          return (
            s.formula_code === formulaCode &&
            s.is_purchase_order &&
            !s.exclude_from_calcs &&
            month === currMonth + 1 &&
            year === currYear
          );
        }).reduce((sum, s) => sum + (typeof s.quantity === 'number' ? s.quantity : parseFloat(String(s.quantity)) || 0), 0);

        // Running stock: carryover + POs
        const initialStorage = currentStorage[formulaCode] || 0;
        const rawSupply = initialStorage + rawSupplyPOs;

        // Update carryover for next month
        currentStorage[formulaCode] = Math.max(0, initialStorage + rawSupplyPOs - rawDemand);

        // Convert to base terms
        const baseEquivSupply = rawSupply * ratio;
        const baseEquivDemand = rawDemand * ratio;

        monthItem[`supply_${formulaCode}`] = baseEquivSupply;
        monthItem[`demand_${formulaCode}`] = baseEquivDemand;
        monthItem.supplyByMaterial[formulaCode] = baseEquivSupply;
        monthItem.demandByMaterial[formulaCode] = baseEquivDemand;
        monthItem.rawSupplyByMaterial[formulaCode] = rawSupply;
        monthItem.rawDemandByMaterial[formulaCode] = rawDemand;
        monthItem.ratioByMaterial[formulaCode] = ratio;

        monthTotalSupply += baseEquivSupply;
        monthTotalDemand += baseEquivDemand;
      });

      // Get base hedges for this month (by maturity date)
      const monthHedges = baseOnlyHedges
        .filter(h => {
          const maturityDate = new Date(h.maturity_date);
          return maturityDate.getMonth() === currMonth &&
            maturityDate.getFullYear() === currYear;
        })
        .reduce((sum, h) => sum + (Number(h.hedged_quantity_mt) || 0), 0);

      // Calculate buffer per derived material for this month, converted to base terms
      let monthTotalBuffer = 0;
      derivedMaterials.forEach(dm => {
        const cfg = bufferConfigs[dm.formula_code];
        const ratio = resolveRatio(dm);
        let dmBuffer = cfg.baseAmount;
        if (cfg.lookaheadMonths > 0 && cfg.safetyPercentage > 0) {
          let lookaheadDemand = 0;
          for (let j = 1; j <= cfg.lookaheadMonths; j++) {
            const futDate = new Date(currYear, currMonth + j, 1);
            const fMonth1 = futDate.getMonth() + 1;
            const fYear = futDate.getFullYear();
            lookaheadDemand += Math.abs(demandPositions
              .filter(d => {
                if (!d.delivery_period) return false;
                const [y, m] = d.delivery_period.split('-').map(Number);
                return d.formula_code === dm.formula_code && y === fYear && m === fMonth1;
              })
              .reduce((s, d) => s + (typeof d.quantity === 'number' ? d.quantity : parseFloat(String(d.quantity)) || 0), 0));
          }
          dmBuffer += lookaheadDemand * (cfg.safetyPercentage / 100);
        }
        monthItem.rawBufferByMaterial[dm.formula_code] = dmBuffer;
        monthItem.bufferByMaterial[dm.formula_code] = dmBuffer * ratio;
        monthTotalBuffer += dmBuffer * ratio;
      });

      monthItem.hedges = monthHedges;
      monthItem.totalSupply = monthTotalSupply;
      monthItem.totalDemand = monthTotalDemand;
      monthItem.buffer = monthTotalBuffer;
      monthItem.fullDemand = monthTotalDemand + monthTotalBuffer;
      monthItem.totalCoverage = monthTotalSupply + monthHedges;
      monthItem.covPercentage = monthItem.fullDemand > 0
        ? Math.round((monthTotalSupply / monthItem.fullDemand) * 100)
        : 0;
      monthItem.hedgedPercentage = monthItem.fullDemand > 0
        ? Math.round((monthHedges / monthItem.fullDemand) * 100)
        : 0;
      monthItem.uncoveredDemand = Math.max(0, monthItem.fullDemand - monthItem.totalCoverage);

      result.push(monthItem);
    }

    // Aggregate into coarser buckets if granularity is not Month
    const bmAggregateBucket = (items: BaseMaterialMonthData[], label: string, absoluteMonth: number, running: Record<string, number>) => {
      const nextRunning = { ...running };
      const termItem: BaseMaterialMonthData = {
        month: label, totalSupply: 0, totalDemand: 0, buffer: 0, fullDemand: 0,
        hedges: 0, totalCoverage: 0, covPercentage: 0, hedgedPercentage: 0, uncoveredDemand: 0,
        supplyByMaterial: {}, demandByMaterial: {},
        rawSupplyByMaterial: {}, rawDemandByMaterial: {},
        bufferByMaterial: {}, rawBufferByMaterial: {}, ratioByMaterial: {},
        absoluteMonth,
      };

      let termTotalSupply = 0, termTotalDemand = 0;

      derivedMaterials.forEach(dm => {
        const code = dm.formula_code;
        const ratio = resolveRatio(dm);
        const initialStorage = running[code] || 0;
        let totalRawSupplyPOs = 0, totalRawDemand = 0;
        let termStorage = initialStorage;

        items.forEach(mi => {
          const mDemand = mi.rawDemandByMaterial?.[code] || 0;
          const mRawSupply = mi.rawSupplyByMaterial?.[code] || 0;
          const mPOs = Math.max(0, mRawSupply - termStorage);
          totalRawDemand += mDemand;
          totalRawSupplyPOs += mPOs;
          termStorage = Math.max(0, termStorage + mPOs - mDemand);
        });

        const termRawSupply = initialStorage + totalRawSupplyPOs;
        const baseEquivSupply = termRawSupply * ratio;
        const baseEquivDemand = totalRawDemand * ratio;

        termItem[`supply_${code}`] = baseEquivSupply;
        termItem[`demand_${code}`] = baseEquivDemand;
        termItem.supplyByMaterial[code] = baseEquivSupply;
        termItem.demandByMaterial[code] = baseEquivDemand;
        termItem.rawSupplyByMaterial[code] = termRawSupply;
        termItem.rawDemandByMaterial[code] = totalRawDemand;
        termItem.ratioByMaterial[code] = ratio;
        const lastItem = items[items.length - 1];
        const termRawBuffer = lastItem?.rawBufferByMaterial?.[code] || 0;
        termItem.rawBufferByMaterial[code] = termRawBuffer;
        termItem.bufferByMaterial[code] = termRawBuffer * ratio;
        termTotalSupply += baseEquivSupply;
        termTotalDemand += baseEquivDemand;
        nextRunning[code] = termStorage;
      });

      const termHedges = items.reduce((s, mi) => s + (mi.hedges || 0), 0);
      const termBuffer = items[items.length - 1]?.buffer || 0;
      termItem.hedges = termHedges;
      termItem.totalSupply = termTotalSupply;
      termItem.totalDemand = termTotalDemand;
      termItem.buffer = termBuffer;
      termItem.fullDemand = termTotalDemand + termBuffer;
      termItem.totalCoverage = termTotalSupply + termHedges;
      termItem.covPercentage = termItem.fullDemand > 0
        ? Math.round((termTotalSupply / termItem.fullDemand) * 100) : 0;
      termItem.hedgedPercentage = termItem.fullDemand > 0
        ? Math.round((termHedges / termItem.fullDemand) * 100) : 0;
      termItem.uncoveredDemand = Math.max(0, termItem.fullDemand - termItem.totalCoverage);

      return { row: termItem, nextState: nextRunning };
    };

    const bmOnHand = result[0];
    const bmInitStorage: Record<string, number> = {};
    derivedMaterials.forEach(dm => {
      bmInitStorage[dm.formula_code] = bmOnHand?.rawSupplyByMaterial?.[dm.formula_code] || 0;
    });

    if (timeGranularity === 'Full') {
      const aggregated = aggregateToSingleBucket<BaseMaterialMonthData, Record<string, number>>({
        monthlyData: result.slice(1),
        initialState: bmInitStorage,
        aggregateBucket: bmAggregateBucket,
      });
      return [bmOnHand, ...aggregated];
    }

    const bmBoundaryMonths = resolveBoundaryMonths(timeGranularity, terminals, monthsToCalculate);
    if (bmBoundaryMonths) {
      const aggregated = aggregateToBoundaryPeriods<BaseMaterialMonthData, Record<string, number>>({
        monthlyData: result.slice(1),
        boundaryMonths: bmBoundaryMonths,
        horizonMonths: monthsToCalculate,
        initialState: bmInitStorage,
        aggregateBucket: bmAggregateBucket,
      });
      return [bmOnHand, ...aggregated];
    }

    return result;
  }, [derivedMaterials, baseTemplate, demandPositions, supplyPositions, baseHedges, pricesMap, terminals, timeGranularity, account.formulasToAccount]);

  // Notify parent when data changes (use ref for callback to avoid infinite loop)
  const onDataCalculatedRef = React.useRef(onDataCalculated);
  onDataCalculatedRef.current = onDataCalculated;
  React.useEffect(() => {
    if (onDataCalculatedRef.current && chartData.length > 0 && derivedMaterials && derivedMaterials.length > 0) {
      onDataCalculatedRef.current({
        chartData,
        derivedMaterials: derivedMaterials.map(dm => ({
          formula_code: dm.formula_code,
          formula_name: dm.formula_name,
          price_ratio: dm.price_ratio,
        })),
      });
    }
  }, [chartData, derivedMaterials]);

  // Prepare datasets: supply bars per derived material + hedges bar + demand line
  const datasets = useMemo<DatasetConfig[]>(() => {
    if (!derivedMaterials || derivedMaterials.length === 0) return [];

    const ds: DatasetConfig[] = [];

    // Supply bars per derived material (stacked)
    derivedMaterials.forEach(dm => {
      const color = formulaColorDictionary[dm.formula_code] || '#6B7280';
      ds.push({
        label: `Supply: ${dm.formula_name}`,
        dataKey: `supply_${dm.formula_code}`,
        type: 'bar',
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        borderRadius: 4,
        order: 1,
      });
    });

    // Hedges bar (stacked with supply)
    ds.push({
      label: 'Hedges (Base)',
      dataKey: 'hedges',
      type: 'bar',
      backgroundColor: '#93C5FD',
      borderColor: '#3B82F6',
      borderWidth: 0,
      borderRadius: 4,
      order: 1,
    });

    // Uncovered demand bar (grey, stacked on top)
    ds.push({
      label: 'Unhedged Exposure',
      dataKey: 'uncoveredDemand',
      type: 'bar',
      backgroundColor: '#cfcfcf',
      borderColor: '#9f9f9f',
      borderWidth: 0,
      borderRadius: 4,
      order: 1,
    });

    // Total demand line (red) — order 0 so it draws on top of bars
    ds.push({
      label: 'Demand + Buffer (base equiv.)',
      dataKey: 'fullDemand',
      type: 'line',
      borderColor: '#EF4444',
      backgroundColor: 'transparent',
      borderWidth: 2,
      pointRadius: 3,
      fill: false,
      hidden: false,
      order: 0,
    });

    return ds;
  }, [derivedMaterials, formulaColorDictionary]);

  // Plugin to color On-Hand bar green
  const pluginsFactory = useMemo(
    () => (_isQuantity: boolean, _matPrice: number) => {
      const onHandColorPlugin = {
        id: 'baseMaterialOnHandColor',
        afterDatasetsDraw(chart: any) {
          const ctx = chart.ctx;
          // Color all supply datasets' first bar with green border
          chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            if (meta && meta.data && meta.data.length > 0 && !meta.hidden && dataset.label?.startsWith('Supply:')) {
              const firstBar = meta.data[0];
              if (firstBar && firstBar.y <= firstBar.base) {
                ctx.save();
                const { x, y, base, width } = firstBar;
                const barX = x - width / 2;
                const barY = y;
                const radius = 4;

                // Draw green border
                ctx.strokeStyle = '#10B981';
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.moveTo(barX, base);
                ctx.lineTo(barX, barY + radius);
                ctx.quadraticCurveTo(barX, barY, barX + radius, barY);
                ctx.lineTo(barX + width - radius, barY);
                ctx.quadraticCurveTo(barX + width, barY, barX + width, barY + radius);
                ctx.lineTo(barX + width, base);
                ctx.stroke();
                ctx.restore();
              }
            }
          });
        },
      };
      return [onHandColorPlugin];
    },
    [],
  );

  if (!derivedMaterials || derivedMaterials.length === 0) {
    return (
      <div className="flex flex-col h-full p-4 items-center justify-center text-gray-500">
        <p className="text-sm">No derived materials configured.</p>
        <p className="text-xs mt-2">Configure base and derived materials in the Hedges Deriving tab.</p>
      </div>
    );
  }

  return (
    <StrategyChart
      data={chartData}
      datasets={datasets}
      matPrice={baseMaterialPrice}
      unit={unit || { displayName: 'Metric Tons', shortForm: 'MT', convertPrice: (v: number) => v, convertMetricTonToUnit: (v: number) => v }}
      onTimeChange={onTimeChange || (() => {})}
      formulaId={baseMaterialId}
      stacked={true}
      showPercentageLabels={true}
      pluginsFactory={pluginsFactory}
      extraParams={{ baseMaterial: true }}
      selectedIndex={selectedIndex != null ? selectedIndex + 1 : undefined}
      onBarClick={(index) => {
        if (onMonthSelect) {
          const adjustedIndex = Math.max(0, index - 1);
          onMonthSelect(adjustedIndex);
        }
      }}
      renderControls={({ isQuantity, setIsQuantity, timeGranularity, setTimeGranularity, viewMonths, setViewMonths }) => (
        <ChartControls
          title={`${baseTemplate?.formula_name || 'Base Material'} — Aggregated View`}
          subtitle="Supply &amp; demand of derived materials converted to base terms (× price ratio)"
          isQuantity={isQuantity}
          setIsQuantity={setIsQuantity}
          timeGranularity={timeGranularity}
          setTimeGranularity={setTimeGranularity}
          viewMonths={viewMonths}
          setViewMonths={setViewMonths}
          granularityOptions={terminalCount > 0 && terminalCount < 12 ? ['Month', 'Quarter', 'Terminal', 'Yearly', 'Full'] : ['Month', 'Quarter', 'Yearly', 'Full']}
          hideSubtitleInQtyMode={false}
        />
      )}
    />
  );
};
